import { registerLocaleData } from '@angular/common';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ErrorHandler,
  LOCALE_ID,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  Router,
  RouterModule,
  withViewTransitions,
} from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import {
  LOCAL_STORAGE_ENGINE,
  NgxsStoragePluginModule,
} from '@ngxs/storage-plugin';
import { NgxsDevelopmentModule, NgxsModule } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { INTL_LOCALES, IntlModule } from 'angular-ecmascript-intl';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { CookieService } from 'ngx-cookie-service';
import { NgxStripeModule } from 'ngx-stripe';
import { providePrimeNG } from 'primeng/config';
import * as superjson from 'superjson';
import { AppComponent } from './app/app.component';
import { GraphQLModule } from './app/graphql.module';
import { PagesModule } from './app/pages/pages.module';
import { skipNonPageTransitions } from './app/pages/route-transitions';
import { routes } from './app/pages/routes';
import { KratosAuthenticationService } from './app/services/authentication/kratos.service';
import { ImpersonationService } from './app/services/impersonation/impersonation.service';
import { FCMService } from './app/services/notifications/fcm.service';
import { ObservabilityService } from './app/services/observability/observability.service';
import { ToastService } from './app/services/toast/toast.service';
import { FeaturesStateModule } from './app/store/features/features.state.module';
import { IncidentActivityStateModule } from './app/store/incident-activity/incident-activity.state.module';
import { MiscState } from './app/store/misc/misc.state';
import { NotificationsState } from './app/store/notifications/notifications.state';
import { NotificationsStateModule } from './app/store/notifications/notifications.state.module';
import { OnboardingStateModule } from './app/store/onboarding/onboarding.state.module';
import { ServicesStateModule } from './app/store/services/services.state.module';
import { SubscriptionStateModule } from './app/store/subscription/subscription.state.module';
import { TrackingStateModule } from './app/store/tracking/tracking.state.module';
import { UISettingsState } from './app/store/ui-settings/ui-settings.state';
import { UserState } from './app/store/user/user.state';
import { UserStateModule } from './app/store/user/user.state.module';
import { environment } from './environments/environment';

import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { provideTranslocoPreloadLangs } from '@jsverse/transloco-preload-langs';

import posthog from 'posthog-js';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './app/services/translation/loader.service';
import { TranslationService } from './app/services/translation/translation.service';
import { OAuth2StateModule } from './app/store/oauth2/oauth2.state.module';
import { InciditeThemePreset } from './primeng-preset';

registerLocaleData(localeDe);
registerLocaleData(localeEn);

if (environment.posthog.enabled) {
  posthog.init(environment.posthog.key, {
    api_host: environment.posthog.host,
    ui_host: environment.posthog.ui_host,
    // anonymous visitors (e.g. on /register) get no person profile until
    // they sign up and are identified
    person_profiles: 'identified_only',
    opt_in_site_apps: true,
    capture_pageview: 'history_change',
  });
}

if (environment.sentry.enabled) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    tunnel: environment.sentry.tunnel,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracePropagationTargets: [environment.gqlEndpoint],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(
      routes,
      withViewTransitions({
        skipInitialTransition: true,
        onViewTransitionCreated: skipNonPageTransitions,
      })
    ),
    importProvidersFrom(
      BrowserModule,
      NgxsModule.forRoot([MiscState, UISettingsState], {
        selectorOptions: {
          suppressErrors: true,
        },
      }),
      NgxsDevelopmentModule.forRoot({
        warnOnUnhandledActions: true,
      }),
      NgxsRouterPluginModule.forRoot(),
      NgxsStoragePluginModule.forRoot({
        keys: [
          {
            key: UserState,
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: UISettingsState,
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: NotificationsState,
            engine: LOCAL_STORAGE_ENGINE,
          },
          {
            key: 'onboarding.local',
            engine: LOCAL_STORAGE_ENGINE,
          },
        ],
        serialize: obj => {
          return superjson.stringify(obj);
        },
        deserialize: obj => {
          return superjson.parse(obj);
        },
      }),
      NgxsFormPluginModule.forRoot(),
      NgxsReduxDevtoolsPluginModule.forRoot({
        latency: 0,
      }),
      RouterModule,
      PagesModule,
      GraphQLModule,
      IntlModule,
      UserStateModule,
      OAuth2StateModule,
      FeaturesStateModule,
      ServicesStateModule,
      TrackingStateModule,
      FontAwesomeModule,
      NgxStripeModule.forRoot(),
      GoogleTagManagerModule.forRoot({
        id: environment.gtm.id,
        gtm_preview: environment.gtm.previewEnv,
        gtm_auth: environment.gtm.auth,
      }),
      NotificationsStateModule,
      SubscriptionStateModule,
      IncidentActivityStateModule,
      OnboardingStateModule
    ),
    KratosAuthenticationService,
    ToastService,
    CookieService,
    ObservabilityService,
    FCMService,
    // Sentry error tracking
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => inject(ImpersonationService).init()),
    {
      provide: LOCALE_ID,
      useValue: 'en-US',
    },
    {
      provide: INTL_LOCALES,
      useValue: ['en-US'],
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideTransloco({
      config: {
        availableLangs: ['en', 'de'],
        defaultLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          allowEmpty: true,
          logMissingKey: false,
        },
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat(),
    provideTranslocoPreloadLangs(['en', 'de']),
    provideAppInitializer(() => {
      // Block bootstrap until the active language is loaded. Without this,
      // components that call `transloco.translate()` synchronously during
      // construction can race the i18n HTTP load and render raw
      // translation keys.
      const transloco = inject(TranslocoService);
      // Instantiating TranslationService runs its constructor, which picks
      // the active language from user/membership/localStorage/browser and
      // calls setActiveLang on Transloco — so load() below targets the
      // right language.
      inject(TranslationService);
      return firstValueFrom(transloco.load(transloco.getActiveLang()));
    }),
    provideTranslocoLocale({
      langToLocaleMapping: {
        en: 'en-US',
        de: 'de-DE',
      },
    }),
    provideFirebaseApp(() =>
      initializeApp({
        projectId: 'incidite-687a5',
        appId: '1:221325142302:web:5e1d1970d032d9bfeac10f',
        storageBucket: 'incidite-687a5.firebasestorage.app',
        apiKey: 'AIzaSyBjD8VycHJLLnrHsKbzqQA2nTFKtuZvA04',
        authDomain: 'incidite-687a5.firebaseapp.com',
        messagingSenderId: '221325142302',
      })
    ),
    provideMessaging(() => getMessaging()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: InciditeThemePreset,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
  ],
}).catch(err => console.error(err));
